/**
 * React Query Key Factories
 *
 * Hierarchical query keys for all warehouse domains to enable granular cache invalidation
 */

import type { PaginationParams } from '../api/client';

/**
 * User query keys (tenant users, e.g. for assignment dropdowns)
 */
export const userKeys = {
  all: ['users'] as const,
};

/**
 * Warehouse query keys
 */
export const warehouseKeys = {
  all: ['warehouses'] as const,
  lists: () => [...warehouseKeys.all, 'list'] as const,
  list: (params?: PaginationParams) => [...warehouseKeys.lists(), params] as const,
  details: () => [...warehouseKeys.all, 'detail'] as const,
  detail: (id: string) => [...warehouseKeys.details(), id] as const,
  kpis: () => [...warehouseKeys.all, 'kpis'] as const,
  locations: (warehouseId: string) => [...warehouseKeys.all, warehouseId, 'locations'] as const,
  inventory: (warehouseId: string) => [...warehouseKeys.all, warehouseId, 'inventory'] as const,
  movements: (warehouseId: string) => [...warehouseKeys.all, warehouseId, 'movements'] as const,
  tasks: (warehouseId: string) => [...warehouseKeys.all, warehouseId, 'tasks'] as const,
};

/**
 * Location query keys
 */
export const locationKeys = {
  all: ['locations'] as const,
  lists: (warehouseId: string) => [...locationKeys.all, 'list', warehouseId] as const,
  list: (warehouseId: string, params?: PaginationParams) =>
    [...locationKeys.lists(warehouseId), params] as const,
  details: () => [...locationKeys.all, 'detail'] as const,
  detail: (id: string) => [...locationKeys.details(), id] as const,
  stock: (locationId: string) => [...locationKeys.all, locationId, 'stock'] as const,
  kpis: (warehouseId: string) => [...locationKeys.all, 'kpis', warehouseId] as const,
};

/**
 * Inventory/Location Stock query keys
 */
export const inventoryKeys = {
  all: ['warehouse-inventory'] as const,
  byWarehouse: (warehouseId: string, params?: PaginationParams) =>
    [...inventoryKeys.all, 'warehouse', warehouseId, params] as const,
  byLocation: (locationId: string, params?: PaginationParams) =>
    [...inventoryKeys.all, 'location', locationId, params] as const,
  detail: (locationId: string, productId: string) =>
    [...inventoryKeys.all, 'detail', locationId, productId] as const,
  kpis: (warehouseId: string) => [...inventoryKeys.all, 'kpis', warehouseId] as const,
};

/**
 * Stock Movement query keys
 */
export const stockMovementKeys = {
  all: ['stock-movements'] as const,
  byWarehouse: (warehouseId: string, params?: PaginationParams) =>
    [...stockMovementKeys.all, 'warehouse', warehouseId, params] as const,
  byOrder: (orderId: string, params?: PaginationParams) =>
    [...stockMovementKeys.all, 'order', orderId, params] as const,
  details: () => [...stockMovementKeys.all, 'detail'] as const,
  detail: (id: string) => [...stockMovementKeys.details(), id] as const,
  kpis: (warehouseId: string) => [...stockMovementKeys.all, 'kpis', warehouseId] as const,
};

/**
 * Warehouse Task query keys
 */
export const taskKeys = {
  all: ['warehouse-tasks'] as const,
  allWarehouses: (params?: PaginationParams) =>
    [...taskKeys.all, 'all-warehouses', params] as const,
  byWarehouse: (warehouseId: string, params?: PaginationParams) =>
    [...taskKeys.all, 'warehouse', warehouseId, params] as const,
  byAssignee: (userId: string, params?: PaginationParams) =>
    [...taskKeys.all, 'assignee', userId, params] as const,
  details: () => [...taskKeys.all, 'detail'] as const,
  detail: (id: string) => [...taskKeys.details(), id] as const,
  allWarehousesKpis: () => [...taskKeys.all, 'kpis', 'all-warehouses'] as const,
  kpis: (warehouseId: string) => [...taskKeys.all, 'kpis', warehouseId] as const,
  byAssigneeKpis: (userId: string) => [...taskKeys.all, 'kpis', 'assignee', userId] as const,
};

/**
 * Stock Allocation query keys
 */
export const allocationKeys = {
  all: ['warehouse-allocations'] as const,
  list: (params?: PaginationParams) =>
    [...allocationKeys.all, 'all', params] as const,
  allKpis: () => [...allocationKeys.all, 'kpis', 'all'] as const,
  byWarehouse: (warehouseId: string, params?: PaginationParams) =>
    [...allocationKeys.all, 'warehouse', warehouseId, params] as const,
  byProduct: (productId: string, params?: PaginationParams) =>
    [...allocationKeys.all, 'product', productId, params] as const,
  byOrder: (orderId: string, params?: PaginationParams) =>
    [...allocationKeys.all, 'order', orderId, params] as const,
  details: () => [...allocationKeys.all, 'detail'] as const,
  detail: (id: string) => [...allocationKeys.details(), id] as const,
  kpis: (warehouseId: string) => [...allocationKeys.all, 'kpis', warehouseId] as const,
};

/**
 * Stock Reservation query keys
 */
export const reservationKeys = {
  all: ['warehouse-reservations'] as const,
  list: (params?: PaginationParams) =>
    [...reservationKeys.all, 'all', params] as const,
  allKpis: () => [...reservationKeys.all, 'kpis', 'all'] as const,
  byWarehouse: (warehouseId: string, params?: PaginationParams) =>
    [...reservationKeys.all, 'warehouse', warehouseId, params] as const,
  byProduct: (productId: string, params?: PaginationParams) =>
    [...reservationKeys.all, 'product', productId, params] as const,
  byCustomer: (customerId: string, params?: PaginationParams) =>
    [...reservationKeys.all, 'customer', customerId, params] as const,
  byOrder: (orderId: string, params?: PaginationParams) =>
    [...reservationKeys.all, 'order', orderId, params] as const,
  details: () => [...reservationKeys.all, 'detail'] as const,
  detail: (id: string) => [...reservationKeys.details(), id] as const,
  kpis: (warehouseId: string) => [...reservationKeys.all, 'kpis', warehouseId] as const,
};
